import type { InvitationDto, StudentParentsDto, UserDto } from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/client';
import { useApiMutation } from '../api/hooks';
import { DataTable } from './DataTable';
import { Badge, Card, ConfirmButton, ErrorMessage, Field, formatDate } from './ui';

/**
 * The parents following a student, with Add a parent. An existing adult account is linked straight
 * away (including a parent who trains or teaches here); anyone else is invited and follows the child
 * once they accept.
 */
export function ParentsCard({
  studentId,
  studentName,
}: {
  studentId: string;
  studentName: string;
}) {
  const key = ['admin', 'parents', studentId];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api.request<StudentParentsDto>(`/admin/students/${studentId}/parents`),
  });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const add = useApiMutation<void, { linked?: UserDto; invitation?: InvitationDto }>(
    () => ({ path: `/admin/students/${studentId}/parents`, method: 'POST', body: { email, name } }),
    [key, ['admin', 'invitations']],
  );
  const unlink = useApiMutation<string>(
    (parentId) => ({ path: `/admin/students/${studentId}/parents/${parentId}`, method: 'DELETE' }),
    [key],
  );
  const rows = [
    ...(list.data?.parents ?? []).map((p) => ({ kind: 'parent' as const, id: p.id, user: p })),
    ...(list.data?.pending ?? []).map((i) => ({
      kind: 'invited' as const,
      id: i.id,
      invitation: i,
    })),
  ];
  return (
    <Card title="Parents">
      <p className="muted">
        Parents follow {studentName}&rsquo;s progress in the app and can show their QR code in
        class. A parent who trains or teaches here follows them from their own account: use the
        email they sign in with.
      </p>
      <ErrorMessage error={list.error ?? add.error ?? unlink.error} />
      {result ? (
        <div className="alert success" role="status">
          {result}
        </div>
      ) : null}
      <DataTable
        rows={list.data ? rows : undefined}
        rowKey={(r) => r.id}
        empty="No parents yet."
        columns={[
          {
            key: 'name',
            header: 'Name',
            render: (r) =>
              r.kind === 'invited' ? (
                r.invitation.name
              ) : (
                <span>
                  {r.user.name}
                  {r.user.role !== 'parent' ? (
                    <span className="muted">
                      {' '}
                      · {r.user.role === 'student' ? 'also trains here' : r.user.role}
                    </span>
                  ) : null}
                </span>
              ),
          },
          {
            key: 'email',
            header: 'Email',
            render: (r) => (r.kind === 'parent' ? r.user.email : r.invitation.email),
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) =>
              r.kind === 'parent' ? (
                <Badge value="active" label="following" />
              ) : (
                <span>
                  <Badge value={r.invitation.status} /> until {formatDate(r.invitation.expiresAt)}
                </span>
              ),
          },
          {
            key: 'actions',
            header: '',
            render: (r) =>
              r.kind === 'parent' ? (
                <ConfirmButton confirmLabel="Remove parent?" onConfirm={() => unlink.mutate(r.id)}>
                  Remove
                </ConfirmButton>
              ) : null,
          },
        ]}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate(undefined, {
            onSuccess: (res) => {
              setResult(
                res.linked
                  ? `${res.linked.name} now follows ${studentName}.`
                  : `Invitation sent to ${email}. They'll follow ${studentName} once they accept.`,
              );
              setEmail('');
              setName('');
            },
          });
        }}
      >
        <div className="form-row">
          <Field label="Parent's name">
            {(id) => (
              <input
                id={id}
                type="text"
                required
                maxLength={80}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          <Field label="Parent's email">
            {(id) => (
              <input
                id={id}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
        </div>
        <button className="btn" type="submit" disabled={add.isPending}>
          Add a parent
        </button>
      </form>
    </Card>
  );
}

/** For young kids: a student with no login of their own, followed by a parent. */
export function ManagedChildForm() {
  const [name, setName] = useState('');
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const create = useApiMutation<void, { student: UserDto }>(
    () => ({
      path: '/admin/students/managed',
      method: 'POST',
      body: { name, parentName, parentEmail, ageGroup: 'kids' },
    }),
    [['students'], ['admin', 'invitations']],
  );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(undefined, {
          onSuccess: (res) => {
            setDone(
              `${res.student.name} was added to Kids. ${parentName} gets an invitation (or is linked straight away if they already have an account).`,
            );
            setName('');
            setParentName('');
            setParentEmail('');
          },
        });
      }}
    >
      <p className="muted">
        For young kids without their own email: the child has no login, and their parent follows
        them in the app and shows their QR code in class.
      </p>
      <ErrorMessage error={create.error} />
      {done ? (
        <div className="alert success" role="status">
          {done}
        </div>
      ) : null}
      <div className="form-row">
        <Field label="Child's name">
          {(id) => (
            <input
              id={id}
              type="text"
              required
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Parent's name">
          {(id) => (
            <input
              id={id}
              type="text"
              required
              maxLength={80}
              value={parentName}
              onChange={(e) => setParentName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Parent's email">
          {(id) => (
            <input
              id={id}
              type="email"
              required
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
            />
          )}
        </Field>
      </div>
      <button className="btn primary" type="submit" disabled={create.isPending}>
        Add child
      </button>
    </form>
  );
}
